import React from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import CImage from "@/bit/components/components.c-image";
import TextInfo from "@/bit/components/components.text";
import getColor from "@gcloud-npm/utils.enums.colors";
import DetailCard from "../cms-detail-card";
import Carousel from "@/bit/components/components.carousel";
import emptyCard from "./emptyCard.json";

const IconGlyph = dynamic(() => import("@/bit/components/components.icon-glyph"));

const hasTextContent = (textProps) =>
  !!textProps && typeof textProps.text === "string";

/**
 * Componente que permite crear un carrousel de DetailCard
 */
function DetailCardList(props) {
  const {
    cmsHandler,
    blockName,
    alias,
    carrusel,
    contentLoaded = true,
    CmsAddElement,
    CmsEditableGroupIcons,
    hideEditor = false,
  } = props;
  const color = props.color || "white";
  const backgroundColor = getColor(color);
  const titleProps = props["cms-atom-text-1"];
  const imageProps = props["cms-atom-image-1"];
  const cardsLayout = props["cards-layout"];
  const cardList = props["cms-ml-detail-card-1"];
  const className = "cms-detail-card-list";

  if (
    !cardList ||
    !Array.isArray(cardList) ||
    (cardList.length === 0 && !process.env.NEXT_PUBLIC_ASTRO_MAKER)
  ) {
    return null;
  }

  // Icono Añadir elemento
  const addElementCmp = (index = "start") =>
    CmsAddElement && (
      <CmsAddElement
        key={`detail-card-add-${index}`}
        addProps={{
          index,
          contentId: blockName,
          itemIdDotNotation: `${blockName}.cms-ml-detail-card-1`,
        }}
        cmsHandler={cmsHandler}
      />
    );

  return (
    <section id={blockName || ""}>
      <div
        className="cms-detail-card-list--title"
        style={{ backgroundColor: backgroundColor }}
      >
        <div className="container">
          <div className="title-gap">
            {hasTextContent(titleProps) && (
              <TextInfo {...titleProps} cmsHandler={cmsHandler} />
            )}
          </div>
        </div>
      </div>
      <div className={className} style={{ backgroundColor: backgroundColor }}>
        {/* Imagen de fondo */}
        <CImage
          {...imageProps}
          src={imageProps?.src || ""}
          alt={imageProps?.alt || ""}
          fill
          cmsHandler={cmsHandler}
        />
        {cmsHandler && !hideEditor && (
          <span
            className={"element-editable block edit-component"}
            onClick={() =>
              cmsHandler({
                ...props,
                name: "cms-block.cms-detail-card-list",
              })
            }
          >
            <IconGlyph id="lapiz-m" height={28} color="white" />
          </span>
        )}
        <div className="container">
          <div className="content-gap">
            {/* Card placeholder cuando la lista es vacia */}
            {process.env.NEXT_PUBLIC_ASTRO_MAKER && cardList.length === 0 ? (
              <Carousel maxPointNum={0} breakpoint={768} slideSeparation={16}>
                {addElementCmp("end")}
                <DetailCard detailCardProps={emptyCard} />
              </Carousel>
            ) : (
              <>
                {carrusel === true || carrusel === "true" ? (
                  <Carousel
                    maxPointNum={0}
                    breakpoint={768}
                    slideSeparation={16}
                  >
                    {[
                      addElementCmp("start"),
                      ...cardList.map((item, index) => (
                        <DetailCard
                          detailCardProps={{
                            ...item,
                            layout: cardsLayout,
                          }}
                          cmsHandler={cmsHandler}
                          index={index}
                          alias={alias}
                          blockName={blockName}
                          key={`detail-card-${index}`}
                          contentLoaded={contentLoaded}
                          frontalListToDelete={cardList}
                          CmsEditableGroupIcons={CmsEditableGroupIcons}
                        />
                      )),
                      addElementCmp("end"),
                    ].filter(Boolean)}
                  </Carousel>
                ) : (
                  <div className={`${className}--grid`}>
                    {[
                      addElementCmp("start"),
                      ...cardList.map((item, index) => (
                        <DetailCard
                          detailCardProps={{
                            ...item,
                            layout: cardsLayout,
                          }}
                          index={index}
                          alias={alias}
                          blockName={blockName}
                          cmsHandler={cmsHandler}
                          key={`detail-card-${index}`}
                          contentLoaded={contentLoaded}
                          frontalListToDelete={cardList}
                          CmsEditableGroupIcons={CmsEditableGroupIcons}
                        />
                      )),
                      addElementCmp("end"),
                    ].filter(Boolean)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`.cms-detail-card-list--title {
  background-color: white;
  padding-top: 1rem 0;
  position: relative;
  .title-gap {
    padding-bottom: 1rem;
  }
}

.cms-detail-card-list {
  background-color: white;
  padding-bottom: 1rem;
  position: relative;

  hr.separator {
    color: rgb(221, 221, 221);
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }

  &--grid {
    position: relative;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
    &::-webkit-scrollbar {
      display: none;
    }
  }
  @media screen and (max-width: 768px) {   
    &--grid {
      display: flex;
      gap: 4px;
      overflow-x: auto;
      overflow-y: hidden;
      height: 100%;
      :global(.DetailCard) {
        height: auto !important;
      }
    }
  }
}
`}</style>
    </section>
  );
}

DetailCardList.propTypes = {
  /**
   * {
   *  "info": "Permite heredar cards implementadas en otras fichas, hay que indicarlo empleando la notacion: [alias]::[objectDotPath]::[start|end]",
   *  "kind": "both",
   *  "beautifulName": "Heredar de:"
   * }
   */
  cmsgetfrom: PropTypes.string,
  /**
   * {
   *  "info": "Define si las cards se muestran en carrusel o en grid",
   *  "kind": "both",
   *  "beautifulName": "Carrusel"
   * }
   */
  carrusel: PropTypes.string,
  /**
   * {
   *  "info": "Define el layout de las cards, en caso de querer otro estilo",
   *  "kind": "both",
   *  "beautifulName": "Layout (ancho) de las cards",
   *  "values": "./enums.js:CARDS_LAYOUT"
   * }
   */
  "cards-layout": PropTypes.string,
  /**
   * {
   *  "info": "Color de fondo",
   *  "kind": "both",
   *  "beautifulName": "Color de fondo"
   * }
   */
  color: PropTypes.string,
};

DetailCardList.defaultProps = {
  carrusel: "true",
  color: "white",
  "cards-layout": "default",
};

export default DetailCardList;
